import React, { useState, useEffect } from 'react';
import { getPhotoStudioAppointments } from '../services/photoStudioService';

const StudioTVDisplay = () => {
  const [studioCode, setStudioCode] = useState('BLR13');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  const TIME_SLOTS = [
    { id: '10:00-11:00', start: 600, end: 660 },
    { id: '11:00-12:00', start: 660, end: 720 },
    { id: '12:00-13:00', start: 720, end: 780 },
    { id: '13:00-14:00', start: 780, end: 840 },
    { id: '15:00-16:00', start: 900, end: 960 },
    { id: '16:00-17:00', start: 960, end: 1020 }
  ];

  const fetchData = async () => {
    try {
      const result = await getPhotoStudioAppointments();
      if (result.success) {
        const today = new Date().toDateString();
        const todayBookings = result.data.filter(b =>
          b.siteCode === studioCode &&
          new Date(b.submittedAt || b.createdAt).toDateString() === today &&
          b.status !== 'Cancelled'
        );
        setBookings(todayBookings);
      }
    } catch (error) {
      console.error('TV Display fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const dataInterval = setInterval(fetchData, 15000);
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      clearInterval(dataInterval);
      clearInterval(clockInterval);
    };
  }, [studioCode]);

  // Find current active slot
  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
  const activeSlot = TIME_SLOTS.find(s => currentMinutes >= s.start && currentMinutes < s.end);

  const getSlotBookings = (slotId) => {
    return bookings.filter(b => {
      if (b.timeSlot === slotId) return true;
      if (b.estimatedTime?.includes('|') && b.estimatedTime.split('|')[1] === slotId) return true;
      return false;
    }).sort((a, b) => (a.queuePosition || 0) - (b.queuePosition || 0));
  };

  const currentSlotBookings = activeSlot ? getSlotBookings(activeSlot.id) : [];
  const inProgress = currentSlotBookings.find(b => b.status === 'In Progress');
  const pending = currentSlotBookings.filter(b => b.status === 'Pending');

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#1a1a2e', color: 'white' }}>
        <h2>Loading Studio Display...</h2>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', background: '#1a1a2e', color: 'white', fontFamily: 'Arial, sans-serif', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Top Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 24px',
        background: '#16213e',
        borderBottom: '2px solid #ff9900',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '28px' }}>📸</span>
          <h1 style={{ margin: 0, fontSize: '22px', color: '#ff9900', fontWeight: '600' }}>
            {studioCode} Photo Studio
          </h1>
          <select
            value={studioCode}
            onChange={(e) => { setStudioCode(e.target.value); setLoading(true); }}
            style={{ padding: '4px 8px', borderRadius: '4px', background: '#0f3460', color: 'white', border: '1px solid #444', fontSize: '13px', marginLeft: '10px' }}
          >
            <option value="BLR13">BLR13</option>
            <option value="BLR18">BLR18</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {activeSlot && (
            <div style={{ color: '#aaa', fontSize: '14px' }}>
              Current Slot: <span style={{ color: '#ff9900', fontWeight: 'bold' }}>{activeSlot.id}</span>
            </div>
          )}
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff9900' }}>
            {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
        </div>
      </div>

      {/* Main Content: Left + Right */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* LEFT SIDE — 60% */}
        <div style={{ flex: '0 0 60%', display: 'flex', flexDirection: 'column', borderRight: '2px solid #0f3460' }}>

          {/* TOP LEFT — Currently Being Served */}
          <div style={{
            flex: '0 0 40%',
            background: inProgress
              ? 'linear-gradient(135deg, #0a3d62, #1e5f8a)'
              : 'linear-gradient(135deg, #1a1a2e, #16213e)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            borderBottom: '2px solid #0f3460',
            padding: '20px'
          }}>
            <div style={{ fontSize: '14px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '8px' }}>
              Now Serving
            </div>
            <div style={{
              fontSize: '72px',
              fontWeight: 'bold',
              color: inProgress ? '#ff9900' : '#444',
              lineHeight: 1,
              marginBottom: '10px'
            }}>
              {inProgress ? inProgress.tokenNumber : '---'}
            </div>
            {inProgress && (
              <>
                <div style={{ fontSize: '20px', color: '#ccc' }}>
                  {inProgress.email?.split('@')[0]}
                </div>
                <div style={{ fontSize: '16px', color: '#888', marginTop: '4px' }}>
                  Position #{inProgress.queuePosition} • {inProgress.estimatedTime?.split('|')[0] || ''}
                </div>
              </>
            )}
            {!inProgress && (
              <div style={{ fontSize: '16px', color: '#555', marginTop: '5px' }}>
                {activeSlot ? 'Waiting for next person...' : 'Studio closed right now'}
              </div>
            )}
          </div>

          {/* BOTTOM LEFT — Waiting Queue */}
          <div style={{
            flex: 1,
            background: '#16213e',
            padding: '20px',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', color: '#ffc107' }}>
                ⏳ Waiting ({pending.length})
              </h2>
            </div>

            {pending.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#555', fontSize: '18px', marginTop: '30px' }}>
                No one waiting in queue
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {pending.map((b, i) => (
                  <div key={b.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: i === 0 ? '#1e3a5f' : '#0f3460',
                    borderRadius: '8px',
                    borderLeft: i === 0 ? '4px solid #ffc107' : '4px solid transparent',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: i === 0 ? '#ffc107' : '#0a2647',
                        color: i === 0 ? '#000' : '#aaa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '14px'
                      }}>
                        #{b.queuePosition}
                      </div>
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '16px', color: i === 0 ? '#ffc107' : '#ddd' }}>
                          {b.tokenNumber}
                        </div>
                        <div style={{ fontSize: '13px', color: '#888' }}>
                          {b.email?.split('@')[0]}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '14px', color: '#aaa' }}>
                        {b.estimatedTime?.split('|')[0] || ''}
                      </div>
                      {i === 0 && (
                        <div style={{ fontSize: '11px', color: '#ffc107', marginTop: '2px' }}>
                          NEXT UP
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE — 40% — QR Code */}
        <div style={{
          flex: '0 0 40%',
          background: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: '#232f3e', fontSize: '22px', margin: '0 0 8px 0' }}>
              📱 Scan to Book
            </h2>
            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 30px 0' }}>
              Scan the QR code to book your photo studio appointment
            </p>
            <img
              src="/QR-code.png"
              alt="Scan to book your photo studio appointment"
              style={{
                width: '80%',
                maxWidth: '350px',
                height: 'auto',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
              }}
            />
            <p style={{ color: '#999', fontSize: '13px', marginTop: '25px', lineHeight: '1.5' }}>
              Amazon Photo Studio<br />
              {studioCode} • {currentTime.toLocaleDateString()}
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default StudioTVDisplay;
